// src/components/FormTaxGroup.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  createTributaryGroup,
  getCstsIbsCbs,
  getClassesTributarias,
  getTributaryGroupById,
  updateTributaryGroup,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import {
  CFOP_LIST,
  ORIGEM_MERCADORIA,
  CST_ICMS_LR,
  CST_ICMS_SN,
  IPI_CST,
  PIS_COFINS_CST,
  UFS_BR,
} from '../utils/fiscalConstants';

const REGIME_SN = 1;
const REGIME_NORMAL = 3;

const CSTS_COM_PERCENTUAL = new Set(['000', '010', '011', '200', '220', '221', '222', '620']);
const ACEITA_FCP = new Set([0, 10, 20, 51, 70, 90]);
const ACEITA_FCP_ST = new Set([10, 30, 70, 90, 201, 202, 203, 900]);

const normalizarCst = (cst) => {
  if (cst == null || String(cst).trim() === '') return '';
  return String(cst).trim().padStart(3, '0');
};

const usaPercentuais = (cst) => CSTS_COM_PERCENTUAL.has(normalizarCst(cst));
const aceitaFcp = (cst) => ACEITA_FCP.has(Number(cst));
const aceitaFcpSt = (cst) => ACEITA_FCP_ST.has(Number(cst));

const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800,
    letterSpacing: 0.3,
    px: 2,
    py: 1,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    backgroundColor: 'background.paper',
  },
};

function Section({ title, children, sx }) {
  return (
    <Box sx={{ ...zen.sectionCardSx, ...sx }}>
      <Box sx={zen.sectionTitleSx}>{title}</Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

const ALIQUOTA_FIELDS = new Set([
  'aliquotaIcms', 'aliquotaIpi', 'aliquotaPis', 'aliquotaCofins',
  'aliquotaIbsUf', 'aliquotaCbs', 'adRemIcms', 'percentualFcp', 'percentualFcpSt',
]);

const toNumber = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const sanitizeAliquota = (raw) => {
  let s = String(raw ?? '').replace(/[^\d.,]/g, '').replace(/\./g, ',');
  const first = s.indexOf(',');
  if (first === -1) return s;
  return s.slice(0, first + 1) + s.slice(first + 1).replace(/,/g, '');
};

const formatAliquota = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return String(n).replace('.', ',');
};

const emptyPerfil = (regime) => ({
  regime,
  cfop: regime === REGIME_SN ? '5102' : '',
  cst: regime === REGIME_SN ? 102 : 0,
  ipi: 99,
  pis: 99,
  cofins: 99,
  aliquotaIcms: regime === REGIME_SN ? '18' : '',
  aliquotaIpi: '0',
  aliquotaPis: '0',
  aliquotaCofins: '0',
  substituicaoTributaria: false,
  combustivel: false,
  adRemIcms: '',
  cstIbsCbs: '',
  classTrib: '',
  aliquotaIbsUf: '',
  aliquotaCbs: '',
});

const emptyUf = () => ({
  uf: '',
  cBenef: '',
  percentualFcp: '',
  percentualFcpSt: '',
});

const perfilFromApi = (p, regime) => {
  if (!p) return emptyPerfil(regime);
  const cstIbsCbs = p.cstIbsCbs ?? '';
  const percentuais = usaPercentuais(cstIbsCbs);
  return {
    regime,
    cfop: p.cfop ?? (regime === REGIME_SN ? '5102' : ''),
    cst: Number(p.cst ?? (regime === REGIME_SN ? 102 : 0)),
    ipi: Number(p.ipi ?? 99),
    pis: Number(p.pis ?? 99),
    cofins: Number(p.cofins ?? 99),
    aliquotaIcms: formatAliquota(p.aliquotaIcms ?? 0),
    aliquotaIpi: formatAliquota(p.aliquotaIpi ?? 0),
    aliquotaPis: formatAliquota(p.aliquotaPis ?? 0),
    aliquotaCofins: formatAliquota(p.aliquotaCofins ?? 0),
    substituicaoTributaria: Boolean(p.substituicaoTributaria),
    combustivel: Boolean(p.combustivel),
    adRemIcms: p.adRemIcms != null ? formatAliquota(p.adRemIcms) : '',
    cstIbsCbs,
    classTrib: p.classTrib ?? '',
    aliquotaIbsUf: percentuais ? formatAliquota(p.aliquotaIbsUf ?? '') : '',
    aliquotaCbs: percentuais ? formatAliquota(p.aliquotaCbs ?? '') : '',
  };
};

const ufsFromApi = (list) =>
  (Array.isArray(list) ? list : []).map((u) => ({
    uf: u.uf ?? '',
    cBenef: u.cBenef ?? '',
    percentualFcp: u.percentualFcp != null ? formatAliquota(u.percentualFcp) : '',
    percentualFcpSt: u.percentualFcpSt != null ? formatAliquota(u.percentualFcpSt) : '',
  }));

const perfilInformado = (p) =>
  p && (String(p.cfop || '').trim() !== '' || Number(p.cst) !== 0);

const toPerfilPayload = (p) => {
  const cstIbs = p.cstIbsCbs ? normalizarCst(p.cstIbsCbs) : null;
  const percentuais = usaPercentuais(cstIbs);
  return {
    regime: p.regime,
    cfop: String(p.cfop || '').trim(),
    cst: Number(p.cst),
    ipi: Number(p.ipi),
    pis: Number(p.pis),
    cofins: Number(p.cofins),
    aliquotaIcms: toNumber(p.aliquotaIcms),
    aliquotaIpi: toNumber(p.aliquotaIpi),
    aliquotaPis: toNumber(p.aliquotaPis),
    aliquotaCofins: toNumber(p.aliquotaCofins),
    substituicaoTributaria: Boolean(p.substituicaoTributaria),
    combustivel: Boolean(p.combustivel),
    adRemIcms: p.combustivel && p.adRemIcms !== '' ? toNumber(p.adRemIcms) : null,
    cstIbsCbs: cstIbs,
    classTrib: p.classTrib ? String(p.classTrib).padStart(6, '0') : null,
    aliquotaIbsUf: percentuais && p.aliquotaIbsUf !== '' ? toNumber(p.aliquotaIbsUf) : null,
    aliquotaCbs: percentuais && p.aliquotaCbs !== '' ? toNumber(p.aliquotaCbs) : null,
  };
};

const toUfsPayload = (ufs) =>
  (ufs || [])
    .filter((u) => String(u.uf || '').trim().length === 2)
    .map((u) => ({
      uf: String(u.uf).trim().toUpperCase(),
      cBenef: String(u.cBenef || '').trim() || null,
      percentualFcp: u.percentualFcp !== '' ? toNumber(u.percentualFcp) : null,
      percentualFcpSt: u.percentualFcpSt !== '' ? toNumber(u.percentualFcpSt) : null,
    }));

const labelCst = (opt) => (opt ? `${opt.codigo} — ${opt.descricao || ''}` : '');
const labelClass = (opt) => {
  if (!opt) return '';
  const nome = opt.nome || opt.descricao || '';
  return `${opt.codigo} — ${nome}`;
};

function PerfilFields({
  perfil,
  setPerfil,
  cstOptions,
  cstLabel,
  csts,
  classes,
  loadingCatalog,
  onCstIbsChange,
  onClassTribChange,
}) {
  const permitePercentuais = usaPercentuais(perfil.cstIbsCbs);
  const cstSelecionado = csts.find((c) => c.codigo === normalizarCst(perfil.cstIbsCbs)) || null;
  const classSelecionada = (() => {
    if (!perfil.classTrib) return null;
    const codigo = String(perfil.classTrib).padStart(6, '0');
    return classes.find((c) => c.codigo === codigo) || null;
  })();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setPerfil((p) => ({ ...p, [name]: checked }));
      return;
    }
    if (['cst', 'ipi', 'pis', 'cofins'].includes(name)) {
      setPerfil((p) => ({ ...p, [name]: toNumber(value) }));
      return;
    }
    if (ALIQUOTA_FIELDS.has(name)) {
      setPerfil((p) => ({ ...p, [name]: sanitizeAliquota(value) }));
      return;
    }
    setPerfil((p) => ({ ...p, [name]: value }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth required={perfil.regime === REGIME_SN}>
          <InputLabel>CFOP</InputLabel>
          <Select label="CFOP" name="cfop" value={perfil.cfop} onChange={handleChange}>
            {perfil.regime === REGIME_NORMAL && (
              <MenuItem value=""><em>— não cadastrado —</em></MenuItem>
            )}
            {CFOP_LIST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={8}>
        <FormControl fullWidth required={perfil.regime === REGIME_SN || perfilInformado(perfil)}>
          <InputLabel>{cstLabel}</InputLabel>
          <Select label={cstLabel} name="cst" value={perfil.cst} onChange={handleChange}>
            {perfil.regime === REGIME_NORMAL && (
              <MenuItem value={0}><em>— não cadastrado —</em></MenuItem>
            )}
            {cstOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          label="Alíq. ICMS (%)"
          name="aliquotaIcms"
          value={perfil.aliquotaIcms}
          onChange={handleChange}
          inputMode="decimal"
          placeholder="0,1"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={8}>
        <FormControl fullWidth>
          <InputLabel>IPI</InputLabel>
          <Select label="IPI" name="ipi" value={perfil.ipi} onChange={handleChange}>
            {IPI_CST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Alíq. IPI (%)"
          name="aliquotaIpi"
          value={perfil.aliquotaIpi}
          onChange={handleChange}
          inputMode="decimal"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={8}>
        <FormControl fullWidth>
          <InputLabel>PIS</InputLabel>
          <Select label="PIS" name="pis" value={perfil.pis} onChange={handleChange}>
            {PIS_COFINS_CST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Alíq. PIS (%)"
          name="aliquotaPis"
          value={perfil.aliquotaPis}
          onChange={handleChange}
          inputMode="decimal"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={8}>
        <FormControl fullWidth>
          <InputLabel>COFINS</InputLabel>
          <Select label="COFINS" name="cofins" value={perfil.cofins} onChange={handleChange}>
            {PIS_COFINS_CST.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Alíq. Cofins (%)"
          name="aliquotaCofins"
          value={perfil.aliquotaCofins}
          onChange={handleChange}
          inputMode="decimal"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Checkbox
              name="substituicaoTributaria"
              checked={Boolean(perfil.substituicaoTributaria)}
              onChange={handleChange}
            />
          }
          label="Substituição tributária"
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="combustivel"
              checked={Boolean(perfil.combustivel)}
              onChange={(e) => {
                const checked = e.target.checked;
                setPerfil((p) => ({
                  ...p,
                  combustivel: checked,
                  cst: checked && p.regime === REGIME_NORMAL ? 61 : p.cst,
                  adRemIcms: checked ? p.adRemIcms : '',
                }));
              }}
            />
          }
          label="Grupo de combustível (ICMS monofásico / CST 61)"
        />
      </Grid>

      {perfil.combustivel && (
        <Grid item xs={12} md={4}>
          <TextField
            label="Alíquota ad rem ICMS (R$/litro)"
            name="adRemIcms"
            value={perfil.adRemIcms}
            onChange={handleChange}
            inputMode="decimal"
            placeholder="1,3720"
            helperText="Valor fixo por unidade"
            fullWidth
            required
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
          Reforma Tributária (IBS / CBS)
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={csts}
          value={cstSelecionado}
          onChange={onCstIbsChange}
          getOptionLabel={labelCst}
          isOptionEqualToValue={(a, b) => a?.codigo === b?.codigo}
          renderInput={(params) => (
            <TextField {...params} label="CST IBS/CBS" helperText="Opcional" />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={classes}
          value={classSelecionada}
          onChange={onClassTribChange}
          disabled={!perfil.cstIbsCbs}
          loading={loadingCatalog}
          getOptionLabel={labelClass}
          isOptionEqualToValue={(a, b) => a?.codigo === b?.codigo}
          renderInput={(params) => (
            <TextField {...params} label="Classificação Tributária" />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Alíq. IBS UF (%)"
          name="aliquotaIbsUf"
          value={permitePercentuais ? perfil.aliquotaIbsUf : ''}
          onChange={handleChange}
          inputMode="decimal"
          fullWidth
          disabled={!permitePercentuais}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Alíq. CBS (%)"
          name="aliquotaCbs"
          value={permitePercentuais ? perfil.aliquotaCbs : ''}
          onChange={handleChange}
          inputMode="decimal"
          fullWidth
          disabled={!permitePercentuais}
        />
      </Grid>
    </Grid>
  );
}

function UfParamsSection({ ufs, setUfs, cst }) {
  const fcpOk = aceitaFcp(cst);
  const fcpStOk = aceitaFcpSt(cst);

  const updateRow = (idx, patch) => {
    setUfs((list) => list.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Parâmetros por UF (cBenef / FCP)
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setUfs((list) => [...list, emptyUf()])}
          sx={{ textTransform: 'none' }}
        >
          Adicionar UF
        </Button>
      </Box>
      {ufs.length === 0 && (
        <FormHelperText sx={{ mb: 1 }}>
          Nenhuma UF cadastrada. Filiais desse regime usam só CFOP/CST do perfil.
        </FormHelperText>
      )}
      {ufs.map((row, idx) => (
        <Grid container spacing={1} key={idx} sx={{ mb: 1, alignItems: 'center' }}>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>UF</InputLabel>
              <Select
                label="UF"
                value={row.uf}
                onChange={(e) => updateRow(idx, { uf: e.target.value })}
              >
                {UFS_BR.map((u) => (
                  <MenuItem key={u.sigla} value={u.sigla}>{u.sigla}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="cBenef"
              value={row.cBenef}
              onChange={(e) => updateRow(idx, { cBenef: e.target.value.toUpperCase() })}
              fullWidth
              placeholder="SEM CBENEF"
              helperText="8/10 chars ou SEM CBENEF"
            />
          </Grid>
          <Grid item xs={5} sm={3}>
            <TextField
              size="small"
              label="% FCP"
              value={row.percentualFcp}
              onChange={(e) => updateRow(idx, { percentualFcp: sanitizeAliquota(e.target.value) })}
              fullWidth
              disabled={!fcpOk}
              helperText={fcpOk ? '' : 'CST não admite FCP'}
            />
          </Grid>
          <Grid item xs={5} sm={2}>
            <TextField
              size="small"
              label="% FCP ST"
              value={row.percentualFcpSt}
              onChange={(e) => updateRow(idx, { percentualFcpSt: sanitizeAliquota(e.target.value) })}
              fullWidth
              disabled={!fcpStOk}
              helperText={fcpStOk ? '' : 'CST não admite FCP ST'}
            />
          </Grid>
          <Grid item xs={2} sm={1}>
            <IconButton
              aria-label="Remover UF"
              onClick={() => setUfs((list) => list.filter((_, i) => i !== idx))}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
}

export default function FormTaxGroup({
  isEditMode = false,
  taxGroupId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const navigate = useNavigate();
  const params = useParams();

  const id = taxGroupId ?? params.id;
  const editing = isEditMode || !!taxGroupId;

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [descricao, setDescricao] = useState('');
  const [origem, setOrigem] = useState(0);
  const [simples, setSimples] = useState(() => emptyPerfil(REGIME_SN));
  const [normal, setNormal] = useState(() => emptyPerfil(REGIME_NORMAL));
  const [ufsSimples, setUfsSimples] = useState([]);
  const [ufsNormal, setUfsNormal] = useState([]);
  const [csts, setCsts] = useState([]);
  const [classesSn, setClassesSn] = useState([]);
  const [classesRn, setClassesRn] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const fechar = useCallback(() => {
    if (onCancel) onCancel();
    else navigate('/tax-groups');
  }, [onCancel, navigate]);

  const carregarClasses = useCallback(async (cst, setter, classTribAtual, setPerfil) => {
    const cstNorm = normalizarCst(cst);
    if (!cstNorm) {
      setter([]);
      return;
    }
    setLoadingCatalog(true);
    try {
      const { data } = await getClassesTributarias(cstNorm);
      const list = Array.isArray(data) ? data : [];
      setter(list);
      if (classTribAtual) {
        const codigo = String(classTribAtual).padStart(6, '0');
        if (!list.some((c) => c.codigo === codigo)) {
          setPerfil((prev) => ({ ...prev, classTrib: '' }));
        }
      }
    } catch (e) {
      setter([]);
      setError(getApiErrorMessage(e, 'Erro ao carregar classificações tributárias.'));
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getCstsIbsCbs();
        setCsts(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(getApiErrorMessage(e, 'Erro ao carregar CSTs IBS/CBS.'));
      }
    })();
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!editing || !id) return;
      try {
        const { data } = await getTributaryGroupById(id);
        setDescricao(data.descricao ?? '');
        setOrigem(Number(data.origem ?? 0));
        const sn = perfilFromApi(data.simples, REGIME_SN);
        const rn = perfilFromApi(data.normal, REGIME_NORMAL);
        setSimples(sn);
        setNormal(rn);
        setUfsSimples(ufsFromApi(data.ufsSimples));
        setUfsNormal(ufsFromApi(data.ufsNormal));
        if (sn.cstIbsCbs) await carregarClasses(sn.cstIbsCbs, setClassesSn, sn.classTrib, setSimples);
        if (rn.cstIbsCbs) await carregarClasses(rn.cstIbsCbs, setClassesRn, rn.classTrib, setNormal);
      } catch (e) {
        setError(getApiErrorMessage(e, 'Erro ao carregar o grupo tributário.'));
      }
    };
    run();
  }, [id, editing, carregarClasses]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F10') {
        e.preventDefault();
        document.getElementById('btn-save-tax')?.click();
      } else if (e.key === 'Escape' && !embedded) {
        e.preventDefault();
        fechar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embedded, fechar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (!descricao?.trim()) throw new Error('Descrição é obrigatória.');
      if (!simples.cfop) throw new Error('CFOP do Simples Nacional é obrigatório.');

      const payload = {
        descricao: descricao.trim(),
        origem: Number(origem),
        simples: toPerfilPayload(simples),
        normal: perfilInformado(normal) ? toPerfilPayload(normal) : null,
        ufsSimples: toUfsPayload(ufsSimples),
        ufsNormal: perfilInformado(normal) ? toUfsPayload(ufsNormal) : [],
      };

      if (editing) await updateTributaryGroup(id, payload);
      else await createTributaryGroup(payload);

      if (onSaved) onSaved(editing ? 'Grupo tributário atualizado' : 'Grupo tributário criado');
      else navigate('/tax-groups');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar o grupo tributário.'));
    } finally {
      setSaving(false);
    }
  };

  const perfilAtivo = tab === 0 ? simples : normal;
  const setPerfilAtivo = tab === 0 ? setSimples : setNormal;
  const classesAtivas = tab === 0 ? classesSn : classesRn;
  const setClassesAtivas = tab === 0 ? setClassesSn : setClassesRn;

  const onCstIbsChange = async (_event, option) => {
    const codigo = option?.codigo || '';
    const percentuais = usaPercentuais(codigo);
    setPerfilAtivo((p) => ({
      ...p,
      cstIbsCbs: codigo,
      classTrib: '',
      aliquotaIbsUf: percentuais ? p.aliquotaIbsUf : '',
      aliquotaCbs: percentuais ? p.aliquotaCbs : '',
    }));
    await carregarClasses(codigo, setClassesAtivas, null, setPerfilAtivo);
  };

  const onClassTribChange = (_event, option) => {
    setPerfilAtivo((p) => ({ ...p, classTrib: option?.codigo || '' }));
  };

  const ufsHint = useMemo(() => {
    if (tab === 1 && !perfilInformado(normal)) {
      return 'Cadastre CFOP/CST do Regime Normal para salvar parâmetros por UF deste perfil.';
    }
    return null;
  }, [tab, normal]);

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 960, mx: 'auto' }}>
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2 }}>
          Grupo Tributário
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Section title="Identificação">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Origem da Mercadoria</InputLabel>
                <Select
                  label="Origem da Mercadoria"
                  value={origem}
                  onChange={(e) => setOrigem(toNumber(e.target.value))}
                >
                  {ORIGEM_MERCADORIA.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Section>

        <Section title="Perfis fiscais" sx={{ mt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Simples Nacional" sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab label="Regime Normal" sx={{ textTransform: 'none', fontWeight: 700 }} />
          </Tabs>

          {tab === 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Opcional. Filiais fora do Simples só emitem com este perfil preenchido.
            </Alert>
          )}

          <PerfilFields
            perfil={perfilAtivo}
            setPerfil={setPerfilAtivo}
            cstOptions={tab === 0 ? CST_ICMS_SN : CST_ICMS_LR}
            cstLabel={tab === 0 ? 'CSOSN (ICMS)' : 'CST ICMS'}
            csts={csts}
            classes={classesAtivas}
            loadingCatalog={loadingCatalog}
            onCstIbsChange={onCstIbsChange}
            onClassTribChange={onClassTribChange}
          />

          {ufsHint ? (
            <FormHelperText sx={{ mt: 2 }}>{ufsHint}</FormHelperText>
          ) : (
            <UfParamsSection
              ufs={tab === 0 ? ufsSimples : ufsNormal}
              setUfs={tab === 0 ? setUfsSimples : setUfsNormal}
              cst={perfilAtivo.cst}
            />
          )}
        </Section>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            id="btn-save-tax"
            type="submit"
            variant="contained"
            disabled={saving}
            sx={{
              minWidth: 160,
              background: zen.blue,
              '&:hover': { background: zen.blueDark },
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Gravar (F10)
          </Button>
          <Button
            variant="contained"
            color="inherit"
            onClick={fechar}
            sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar{embedded ? '' : ' (ESC)'}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
